import {
    AvailabilityPattern,
    AvailabilityType,
    IAvailabilityWindowSchema,
    IWorkDaysSchema
} from './availability.model';
import { DateRange } from './date-range.model';
import { HistoryAction } from './history.model';
import {
    IBufferTimes,
    IIncrementStartTime,
    IMaxEventsPerPeriod,
    IMeetingTypeAsk,
    IMeetingTypeConfirmationUX,
    IMeetingTypeField,
    IMeetingTypeLearnedField,
    IStartOffsetStartTime,
    ITrackingId,
    SupportedLanguagesType
} from './meeting-type-fields.model';
import { IUserPaymentProvider } from './user.model';

export enum MeetingTypeStyle {
    Meeting = 'meeting',
    Class = 'class'
}

/*
Both Meeting Types and Class Types are based on MeetingTypeBase, and either can be saved in the new MeetingTypeCommonSchema

                    MeetingTypeCommonBase -- MeetingTypeSchema
                  /                                           \
MeetingTypeBase --                                              MeetingTypeCommonSchema
                  \                                           /
                    ClassTypeBase ---------- ClassTypeSchema
*/

export interface IMeetingTypeCommonSchema {
    name: string;
    users: IMeetingTypeUserSchema[];
    value: IMeetingTypeSchema | IClassTypeSchema;
}

/** Allows updating meeting type state without passing in the entire meeting type object */
export type PartialMeetingType<K extends keyof IMeetingTypeSchema> = Pick<IMeetingTypeSchema, K>;
export type PartialMeetingTypeUser<K extends keyof IMeetingTypeUserSchema> = Pick<IMeetingTypeUserSchema, K>;

export interface IMeetingTypeUserSchema {
    id: string;
    availabilityUser: boolean; // same as `availabilityUserId` in meeting types V1
    owner?: boolean;
    videoProviderStatic?: string;
    videoProviderId?: string;
    bookingCalendarId?: string;

    // Collect Payments
    paymentProvider?: IUserPaymentProvider;
}

export interface IClassTypeSchema extends IClassTypeBase {}

export interface IClassTypeBase extends IMeetingTypeBase {
    style: MeetingTypeStyle.Class;

    // Booking Details
    maxAttendees?: IMeetingTypeField<number>;
}

// temp note: this is equivalent to IMeetingType in meeting-types.model
export interface IMeetingTypeSchema extends IMeetingTypeCommonBase {
    style: MeetingTypeStyle.Meeting;
}

// temp note: this is equivalent to IMeetingTypeCommon in meeting-types.model
export interface IMeetingTypeCommonBase extends IMeetingTypeBase {
    startHour?: IMeetingTypeLearnedField<string>;
    endHour?: IMeetingTypeLearnedField<string>;
    availabilityWindows?: IMeetingTypeField<IAvailabilityWindowSchema>;

    startTime?: IMeetingTypeField<IStartOffsetStartTime | IIncrementStartTime>;

    /**
     * @deprecated Use `availabilityWindows` instead
     */
    days?: Readonly<IMeetingTypeLearnedField<IWorkDaysSchema>>;

    allowEditingLocation?: IMeetingTypeField<boolean>; // Allow invitees to edit meeting location
    disableInvitingAttendees?: IMeetingTypeField<boolean>; // Allows invitees to add additional attendees

    // currently only used in the default meeting type
    dailyFreeRatio?: IMeetingTypeField<number>;
    // currently only used in the default meeting type
    disableTravelTime?: IMeetingTypeField<boolean>;

    noticePeriodMinutes?: IMeetingTypeField<number>; // lead time
    dateRange?: IMeetingTypeField<DateRange>;
    availabilityType?: IMeetingTypeField<AvailabilityType>;
    disableTransferOwnership?: IMeetingTypeField<boolean>;

    bufferTimes?: IMeetingTypeField<IBufferTimes>;

    availabilityPattern?: IMeetingTypeField<AvailabilityPattern>;

    enableRoundRobinSelection?: IMeetingTypeField<boolean>;

    maxEventsPerPeriod?: IMeetingTypeField<IMaxEventsPerPeriod>;

    enableHolidayBlocking?: IMeetingTypeField<boolean>; // Allows ability to automatically remove availabilities based on user country holidays. true = block availabilities from holidays, false = don't block availabilities based on holidays
}

/** This is the foundational model that meetings and classes are built on top */
export interface IMeetingTypeBase {
    locked?: IMeetingTypeField<boolean>;
    datePinnedToDashboard?: IMeetingTypeField<string>;

    // Name and Link
    defaultMeetingSubject?: IMeetingTypeField<string>;
    ondemand?: IMeetingTypeField<string>; // short code for link

    meetingLength?: IMeetingTypeLearnedField<number>;

    // Location & Conferencing
    location?: IMeetingTypeLearnedField<string>;
    roomCapacity?: IMeetingTypeField<number>;

    // Additional Customization
    language?: IMeetingTypeField<SupportedLanguagesType>;
    description?: IMeetingTypeField<string>; // Meeting Instructions
    confirmationUX?: IMeetingTypeField<IMeetingTypeConfirmationUX>;
    ask?: IMeetingTypeField<IMeetingTypeAsk>; // List of questions to ask the meeting attendees

    readonly history: HistoryAction[];

    readonly shareWithNewTeamMembers?: boolean;

    // Tracking Ids such as google analytics
    trackingIds?: IMeetingTypeField<ITrackingId[]>;
}
